/**
 * Storage Service
 * Centralized, type-safe wrapper for localStorage and sessionStorage
 */

// Storage keys enum for type safety and easy refactoring
export const StorageKeys = {
  // Auth & Payment Flow
  PENDING_PAYPAL_REDIRECT: 'pending_paypal_redirect',
  JUST_SIGNED_UP: 'just_signed_up',
  FROM_WELCOME_VIDEO: 'from_welcome_video',
  
  // UI Preferences
  PWA_INSTALL_DISMISSED: 'pwa-install-dismissed',
} as const;

type StorageKey = typeof StorageKeys[keyof typeof StorageKeys];

/**
 * Session Storage Operations
 */
export const sessionStorage = {
  get(key: StorageKey): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      console.error(`[Storage] Failed to get session item "${key}":`, error);
      return null;
    }
  },

  set(key: StorageKey, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      console.error(`[Storage] Failed to set session item "${key}":`, error);
    }
  },

  remove(key: StorageKey): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`[Storage] Failed to remove session item "${key}":`, error);
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.error('[Storage] Failed to clear session storage:', error);
    }
  },

  // Boolean helpers
  getBoolean(key: StorageKey): boolean {
    return this.get(key) === 'true';
  },

  setBoolean(key: StorageKey, value: boolean): void {
    this.set(key, value ? 'true' : 'false');
  },
};

/**
 * Local Storage Operations
 */
export const localStorage = {
  get(key: StorageKey): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.error(`[Storage] Failed to get local item "${key}":`, error);
      return null;
    }
  },

  set(key: StorageKey, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.error(`[Storage] Failed to set local item "${key}":`, error);
    }
  },

  remove(key: StorageKey): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.error(`[Storage] Failed to remove local item "${key}":`, error);
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.clear();
    } catch (error) {
      console.error('[Storage] Failed to clear local storage:', error);
    }
  },

  // Boolean helpers
  getBoolean(key: StorageKey): boolean {
    return this.get(key) === 'true';
  },

  setBoolean(key: StorageKey, value: boolean): void {
    this.set(key, value ? 'true' : 'false');
  },
};
